import React, { FC, LabelHTMLAttributes } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useResponsiveType } from '../../utils';
import { ThemeTypographyKeys } from '../../styles/tokens/typography';
import { ThemeColorsKeys } from '../../styles/tokens/colors';

export type TypographyComponents =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'p'
  | 'span'
  | 'small'
  | 'strong'
  | 'legend'
  | 'label'
  | 'button'
  | 'dt'
  | 'dd'
  | 'li'
  | 'address'
  | 'figcaption'
  | 'div'
  | 'blockquote'
  | undefined;

export interface TypographyProps extends LabelHTMLAttributes<HTMLLabelElement> {
  component?: TypographyComponents;
  variant?: ThemeTypographyKeys;
  color?: ThemeColorsKeys;
  visuallyHidden?: boolean;
  text?: string;
  centered?: boolean;
  underline?: boolean;
}

interface TypographyStylesProps {
  component?: TypographyComponents;
  variant: ThemeTypographyKeys;
  $color?: ThemeColorsKeys;
  $visuallyHidden?: boolean;
  underline?: boolean;
  centered?: boolean;
}

const TypographyStyles = styled.label<TypographyStylesProps>`
  ${({
    theme: { colors },
    variant,
    $color,
    centered,
    $visuallyHidden,
    underline,
  }) => css`
    color: ${$color ? colors[$color] : 'currentColor'};

    // Variants

    ${variant && useResponsiveType(variant)};

    // centered

    ${centered &&
    css`
      text-align: center;
    `}

    // Visually hidden

    ${$visuallyHidden &&
    css`
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border-width: 0;
    `}

    ${underline &&
    css`
      text-decoration: underline;
    `}
  `};
`;

export const Typography: FC<React.PropsWithChildren<TypographyProps>> = ({
  children,
  component = 'p',
  variant = 'bodyMedium',
  color,
  visuallyHidden,
  text,
  centered,
  underline,
  ...rest
}) => (
  <TypographyStyles
    as={component}
    variant={variant}
    $color={color}
    $visuallyHidden={visuallyHidden}
    centered={centered}
    {...rest}
  >
    {text || children}
  </TypographyStyles>
);
